import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './features/auth/AuthContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { SeoHead } from './components/common/SeoHead';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import AdminActivityPage from './pages/admin/AdminActivityPage';
import AdminBrokersPage from './pages/admin/AdminBrokersPage';
import NotFoundPage from './pages/NotFoundPage';
import MemberDashboard from './pages/member/MemberDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import CourseListPage from './pages/public/CourseListPage';
import CourseDetailPage from './pages/public/CourseDetailPage';
import MyCoursesPage from './pages/member/MyCoursesPage';
import OwnedCourseDetailPage from './pages/member/OwnedCourseDetailPage';
import LessonDetailPage from './pages/member/LessonDetailPage';
import AdminCoursesPage from './pages/admin/AdminCoursesPage';
import MemberOrdersPage from './pages/member/MemberOrdersPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import MemberVipPage from './pages/member/MemberVipPage';
import AdminVipPage from './pages/admin/AdminVipPage';
import MemberLiveTradesPage from './pages/member/MemberLiveTradesPage';
import AdminLiveTradesPage from './pages/admin/AdminLiveTradesPage';
import MentorLiveTradesPage from './pages/mentor/MentorLiveTradesPage';
import MemberJournalPage from './pages/member/MemberJournalPage';
import LotCalculatorPage from './pages/member/LotCalculatorPage';
import SustainingPowerPage from './pages/member/SustainingPowerPage';
import AdminJournalReportPage from './pages/admin/AdminJournalReportPage';
import MemberIndicatorPage from './pages/member/MemberIndicatorPage';
import MemberChartPage from './pages/member/MemberChartPage';
import AdminIndicatorPage from './pages/admin/AdminIndicatorPage';
import CopytradePage from './pages/public/CopytradePage';
import MemberCopytradePage from './pages/member/MemberCopytradePage';
import AdminCopytradePage from './pages/admin/AdminCopytradePage';
import HelpPage from './pages/public/HelpPage';
import MemberHelpPage from './pages/member/MemberHelpPage';
import AdminFaqHelpPage from './pages/admin/AdminFaqHelpPage';
import AdminAdsPage from './pages/admin/AdminAdsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminPaymentGatewayPage from './pages/admin/AdminPaymentGatewayPage';
import ProfilePage from './pages/member/ProfilePage';
import NotificationsPage from './pages/member/NotificationsPage';
import { InstallPrompt } from './components/common/InstallPrompt';
import MemberSignalsPage from './pages/member/MemberSignalsPage';
import AdminSignalsPage from './pages/admin/AdminSignalsPage';
import AdminMcpPage from './pages/admin/AdminMcpPage';
import AdminManualPage from './pages/admin/AdminManualPage';
import AdminFeatureAccessPage from './pages/admin/AdminFeatureAccessPage';
import SignalPage from './pages/public/SignalPage';
import OrderDetailPage from './pages/member/OrderDetailPage';

const MEMBER_ROLES = ['member', 'vip', 'mentor', 'sales', 'webinar', 'workshop'] as const;

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <SeoHead />
          <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Public course pages */}
          <Route path="/courses" element={<CourseListPage />} />
          <Route path="/courses/:slug" element={<CourseDetailPage />} />
          <Route path="/copytrade" element={<CopytradePage />} />
          <Route path="/signal/:id" element={<SignalPage />} />
          <Route path="/help" element={<HelpPage />} />

          {/* Member */}
          <Route
            path="/member/dashboard"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/courses"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MyCoursesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/courses/:courseId"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <OwnedCourseDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/courses/:courseId/lessons/:lessonId"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <LessonDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/orders"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/vip"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberVipPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/live-trades"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberLiveTradesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/signals"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberSignalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/journal"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberJournalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/lot-calculator"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <LotCalculatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/sustaining-power"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <SustainingPowerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/indicator-access"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberIndicatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/chart"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberChartPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/copytrade"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberCopytradePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/help"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <MemberHelpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/profile"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/notifications"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/orders/:id"
            element={
              <ProtectedRoute allowedRoles={[...MEMBER_ROLES]}>
                <OrderDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mentor/live-trades"
            element={
              <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                <MentorLiveTradesPage />
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/courses"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCoursesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vip"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminVipPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/live-trades"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLiveTradesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/signals"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSignalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trading-journals"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminJournalReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/indicator-access"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminIndicatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/copytrade"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminCopytradePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/faq-help"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminFaqHelpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ads"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminAdsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/mcp-connector"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminMcpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/feature-access"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminFeatureAccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manual-book"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminManualPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminNotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications-inbox"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <NotificationsPage admin />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/payments-gateway" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPaymentGatewayPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/activity-logs" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminActivityPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/brokers" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminBrokersPage />
            </ProtectedRoute>
          } />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <InstallPrompt />
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </AuthProvider>
    </ErrorBoundary>
  );
}
